<script setup>
import Footer from '../shared/components/FooterSection.vue'
import ContentLibraryItemCard from '../shared/components/ContentLibraryItemCard.vue'
import WhiteLabelModal from '../shared/components/WhiteLabelModal.vue'
import AlertNotification from '../shared/components/AlertNotification.vue'
import BcsTooltip from '../shared/components/BcsTooltip.vue'
import { computed, inject, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import BcsSearchDropdown from '../shared/components/BcsSearchDropdown.vue'
import FilterDropdown from '../shared/components/common/FilterDropdown.vue'
import { createDefaultContentLibraryItemFilters, useContentLibraryItemFilters } from '../shared/composables/useContentLibraryItemFilters'
import { useContentLibraryDownloadAlert } from '../shared/composables/useContentLibraryDownloadAlert'
import { useAgencyBranding } from '../shared/composables/useAgencyBranding'
import { useAuthStore } from '../shared/stores/AuthStore'
import { useSessionStorageStore } from '../shared/stores/SessionStorage'
import { PERMISSION_KEYS } from '../shared/access/permissionKeys'
import { ContentLibraryService } from '../shared/services/ContentLibraryService'
import { ContentLibraryTemplateService } from '../shared/services/ContentLibraryTemplateService'
import { handleErrors } from '../shared/utils/errorHandler'
import { isEmbeddableContentLibraryUrl } from '../shared/utils/contentLibraryHelpers'
import { useContentLibraryDocumentDownloadActions, isBrandableContentLibraryItem } from '../shared/composables/useContentLibraryDocumentDownloadActions'
import whiteLabelingAvailableIcon from '@/assets/images/white-labeling-available.svg'

const customNavigation = inject('customNavigation')
const toggleLoadingWithMessage = inject('toggleLoadingWithMessage', () => { })
const sessionStorageStore = useSessionStorageStore()
const contentLibraryService = ContentLibraryService()
const contentLibraryTemplateService = ContentLibraryTemplateService()
const authStore = useAuthStore()
const { logoPath, logoSize, loadBranding } = useAgencyBranding()
const isContentLibraryLocked = computed(() => (
  !authStore.hasPermission(PERMISSION_KEYS.CONTENT_LIBRARY_ACCESS)
))
const hasCoreAccess = computed(() => authStore.hasPermission(PERMISSION_KEYS.CORE_ACCESS))
const { downloadAlert, dismissDownloadAlert, reportDownloadFailure } = useContentLibraryDownloadAlert()
const {
  getDocumentActions,
  getDocumentActionLabel,
  getDocumentActionIcon
} = useContentLibraryDocumentDownloadActions()
const groupedCategories = ref([])
const filteredCategories = ref([])
const activeSearchItemIds = ref(null)
const loading = ref(false)
const taxonomyGroupDetails = ref(null)
const hasScrolledItems = ref(false)
const showTopButton = ref(false)
const filterDropdownRefs = ref([])
const favoriteItemIds = ref(new Set())
const favoriteActionLoadingIds = ref(new Set())
const downloadStatsByItemId = ref(new Map())
const copyPasswordSnackbarMessage = ref('')
const whiteLabelModalRef = ref(null)
const pendingBrandingDownload = ref(null)
let copyPasswordSnackbarTimeoutId = null

const taxonomyGroupId = computed(() => {
  const fromQuery = customNavigation?.value?.internalQuery?.taxonomyGroupId
  if (fromQuery) return fromQuery
  try {
    const stored = JSON.parse(sessionStorage.getItem('contentLibraryTaxonomyInternalQuery'))
    return stored?.taxonomyGroupId || ''
  } catch {
    return ''
  }
})
const selectedItemId = computed(() => customNavigation?.value?.internalQuery?.itemId || '')
const breadcrumbTitle = computed(() => taxonomyGroupDetails.value?.name || 'Content Library Items')

const getSortableItemTitle = (item) => {
  return item.libraryFileName || item.documentDisplayName || 'Untitled item'
}

const {
  sortByOptions,
  selectedFilters,
  selectedSortBy,
  getFilterDefinitionsWithCounts,
  applyItemViewStateToCategories,
  sortItemsAlphabetically
} = useContentLibraryItemFilters({
  favoriteItemIds,
  downloadStatsByItemId,
  getSortableItemTitle
})

const countedFilterDefinitions = computed(() => {
  return getFilterDefinitionsWithCounts(groupedCategories.value, {
    itemPredicate: (item) => !activeSearchItemIds.value || activeSearchItemIds.value.has(item.id)
  })
})

const hasSelectedFilters = computed(() => {
  return Object.values(selectedFilters.value).some((value) => Boolean(value))
})

const loadDownloadStatsByItemId = async () => {
  try {
    downloadStatsByItemId.value = await contentLibraryService.getDownloadStatsByItemId()
    syncFilteredCategories()
  } catch (error) {
    handleErrors('BCSContentLibraryTaxonomyItems.loadDownloadStatsByItemId', error)
    downloadStatsByItemId.value = new Map()
    syncFilteredCategories()
  }
}

const scrollToSelectedItem = () => {
  if (!selectedItemId.value) {
    return
  }

  requestAnimationFrame(() => {
    const targetElement = document.getElementById(`content-library-item-${selectedItemId.value}`)
    if (targetElement) {
      targetElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
  })
}

const loadFavoriteItemIds = async () => {
  try {
    const itemIds = await contentLibraryService.getFavoriteItemIds()
    favoriteItemIds.value = new Set(itemIds)
    syncFilteredCategories()
  } catch (error) {
    handleErrors('BCSContentLibraryTaxonomyItems.loadFavoriteItemIds', error)
    favoriteItemIds.value = new Set()
    syncFilteredCategories()
  }
}

const syncFilteredCategories = () => {
  const viewStateCategories = applyItemViewStateToCategories(groupedCategories.value)

  if (!activeSearchItemIds.value) {
    filteredCategories.value = viewStateCategories
    return
  }

  filteredCategories.value = viewStateCategories
    .map((category) => ({
      ...category,
      items: (category.items || []).filter((item) => activeSearchItemIds.value.has(item.id))
    }))
    .filter((category) => category.items.length > 0)
}

const searchableItems = computed(() => {
  return applyItemViewStateToCategories(groupedCategories.value).flatMap((category) =>
    category.items.map((item) => ({
      id: item.id,
      title: item.libraryFileName || item.documentDisplayName || 'Untitled item',
      description: item.description || category.name || '',
      taxonomyGroupId: taxonomyGroupId.value
    }))
  )
})

const filteredResultsCount = computed(() => {
  return filteredCategories.value.reduce((total, category) => total + (category.items?.length || 0), 0)
})

const redirectToLockedContentLibrary = () => {
  const navigation = customNavigation?.value
  if (!navigation) {
    return
  }

  navigation.navigate({
    newPage: navigation.routes.contentLibrary.name
  })
}

const loadGroupedItems = async () => {
  if (isContentLibraryLocked.value) {
    redirectToLockedContentLibrary()
    return
  }

  if (!taxonomyGroupId.value) {
    activeSearchItemIds.value = null
    groupedCategories.value = []
    filteredCategories.value = []
    taxonomyGroupDetails.value = null
    return
  }
  loading.value = true
  try {
    const response = await contentLibraryService.getGroupedItemsByTaxonomyGroup(taxonomyGroupId.value)
    const sortedCategories = response.categories.map((category) => ({
      ...category,
      items: sortItemsAlphabetically(category.items || [])
    }))
    taxonomyGroupDetails.value = response.details
    groupedCategories.value = sortedCategories
    syncFilteredCategories()
  } catch (error) {
    handleErrors('BCSContentLibraryTaxonomyItems.loadGroupedItems', error)
    activeSearchItemIds.value = null
    taxonomyGroupDetails.value = null
    groupedCategories.value = []
    filteredCategories.value = []
  } finally {
    loading.value = false
  }
}

const goToHome = () => {
  const navigation = customNavigation?.value
  if (!navigation) {
    return
  }
  navigation.navigate({
    newPage: navigation.routes.Home.name
  })
}

const goToHelpAndResources = () => {
  const navigation = customNavigation?.value
  if (!navigation) {
    return
  }
  navigation.navigate({
    newPage: navigation.routes.helpAndResources.name
  })
}

const goBackToContentLibraryMain = () => {
  const navigation = customNavigation?.value
  if (!navigation) {
    return
  }

  const previousPage = navigation.backStack?.[navigation.backStack.length - 1]
  if (previousPage === navigation.routes.contentLibrary.name) {
    navigation.goBack()
    return
  }

  navigation.navigate({
    newPage: navigation.routes.contentLibrary.name
  })
}

const handleSearch = ({ results, term, source }) => {
  if (source !== 'suggestion') {
    contentLibraryService.recordLibrarySearch({ term, trigger: 'submit' })
  }

  activeSearchItemIds.value = new Set(results.map((item) => item.id))
  syncFilteredCategories()
}

const handleClear = () => {
  activeSearchItemIds.value = null
  syncFilteredCategories()
}

const setFilterDropdownRef = (element, index) => {
  filterDropdownRefs.value[index] = element
}

const resetFilters = () => {
  selectedFilters.value = createDefaultContentLibraryItemFilters()

  filterDropdownRefs.value.forEach((dropdownRef) => {
    dropdownRef?.close?.()
  })
}

const getDownloadPayloadParts = (payload) => {
  if (payload?.item && payload?.action) {
    return payload
  }

  return {
    item: payload,
    action: null
  }
}

const handleDocumentDownload = async (payload) => {
  const { item, action } = getDownloadPayloadParts(payload)
  if (isBrandableContentLibraryItem(item) || action?.isBrandable) {
    handleBrandingDocumentDownload(item, action)
    return
  }

  if (isBrandableContentLibraryItem(item) || !item?.documentFileName) {
    return
  }

  toggleLoadingWithMessage(true, {
    title: 'Downloading document...',
    body: 'Just a few seconds while we prepare the file.'
  })

  try {
    await contentLibraryService.downloadDocument(item)
  } catch (error) {
    handleErrors('BCSContentLibraryTaxonomyItems.handleDocumentDownload', error)
    reportDownloadFailure(error)
    return
  } finally {
    toggleLoadingWithMessage(false, null)
  }

  // Record download activity after the overlay is dismissed
  try {
    const format = item.documentFileName?.split('.').pop()?.toUpperCase() || ''
    const itemWithTaxonomy = { ...item, taxonomyGroupId: taxonomyGroupId.value }
    const nextDownloadStats = await contentLibraryService.recordItemDownload(itemWithTaxonomy, format)

    if (nextDownloadStats) {
      const nextDownloadStatsByItemId = new Map(downloadStatsByItemId.value)
      nextDownloadStatsByItemId.set(item.id, nextDownloadStats)
      downloadStatsByItemId.value = nextDownloadStatsByItemId
      syncFilteredCategories()
    }
  } catch (error) {
    handleErrors('BCSContentLibraryTaxonomyItems.handleDocumentDownload.recordItemDownload', error)
  }
}

const handleBrandingDocumentDownload = (item, action) => {
  if (!isBrandableContentLibraryItem(item) || !action?.format) {
    return
  }

  pendingBrandingDownload.value = { item, action }

  if (logoPath.value) {
    whiteLabelModalRef.value?.openModal()
    return
  }

  downloadPendingBrandingDocument({ printWithCustomLogo: false })
}

const downloadPendingBrandingDocument = async ({ printWithCustomLogo = false } = {}) => {
  const pendingDownload = pendingBrandingDownload.value
  if (!isBrandableContentLibraryItem(pendingDownload?.item) || !pendingDownload?.action?.format) {
    return
  }

  toggleLoadingWithMessage(true, {
    title: 'Preparing your download',
    description: 'Please wait while we get your document ready.'
  })

  try {
    await contentLibraryTemplateService.downloadCachedContentLibraryDocument({
      itemId: pendingDownload.item.id,
      format: pendingDownload.action.format,
      item: pendingDownload.item,
      whiteLabel: printWithCustomLogo
    })
  } catch (error) {
    handleErrors('BCSContentLibraryTaxonomyItems.downloadPendingBrandingDocument', error)
    reportDownloadFailure(error, { isWhiteLabel: printWithCustomLogo })
    return
  } finally {
    toggleLoadingWithMessage(false, null)
    closeWhiteLabelModal()
  }

  // Record download activity after the overlay is dismissed
  try {
    const format = pendingDownload.action?.format?.toUpperCase() || ''
    const itemWithTaxonomy = { ...pendingDownload.item, taxonomyGroupId: taxonomyGroupId.value }
    const nextDownloadStats = await contentLibraryService.recordItemDownload(itemWithTaxonomy, format)

    if (nextDownloadStats) {
      const nextDownloadStatsByItemId = new Map(downloadStatsByItemId.value)
      nextDownloadStatsByItemId.set(pendingDownload.item.id, nextDownloadStats)
      downloadStatsByItemId.value = nextDownloadStatsByItemId
      syncFilteredCategories()
    }
  } catch (error) {
    handleErrors('BCSContentLibraryTaxonomyItems.downloadPendingBrandingDocument.recordItemDownload', error)
  }
}

const pendingBrandingDownloadFileType = computed(() => {
  return pendingBrandingDownload.value?.action?.format?.toUpperCase() || 'PDF'
})

const closeWhiteLabelModal = () => {
  whiteLabelModalRef.value?.closeModal()
  pendingBrandingDownload.value = null
}

const toggleFavoriteItem = async (item) => {
  if (!item?.id || isFavoriteActionLoading(item.id)) {
    return
  }

  favoriteActionLoadingIds.value.add(item.id)
  try {
    if (isFavoriteItem(item.id)) {
      await contentLibraryService.removeFavoriteItem(item.id)
      favoriteItemIds.value.delete(item.id)
      await contentLibraryService.recordLastLibraryActivity({
        type: 'favorite_removed',
        item
      })
    } else {
      const itemWithTaxonomy = { ...item, taxonomyGroupId: taxonomyGroupId.value }
      await contentLibraryService.addFavoriteItem(itemWithTaxonomy)
      favoriteItemIds.value.add(item.id)
      await contentLibraryService.recordLastLibraryActivity({
        type: 'favorite_added',
        item: itemWithTaxonomy
      })
    }

    favoriteItemIds.value = new Set(favoriteItemIds.value)
    syncFilteredCategories()
  } catch (error) {
    handleErrors('BCSContentLibraryTaxonomyItems.toggleFavoriteItem', error)
  } finally {
    favoriteActionLoadingIds.value.delete(item.id)
    favoriteActionLoadingIds.value = new Set(favoriteActionLoadingIds.value)
  }
}

const handleSelect = (item, typedTerm) => {
  contentLibraryService.recordLibrarySearch({
    term: item?.title,
    trigger: 'suggestion',
    itemId: item?.id,
    typedTerm
  })

  const targetElement = document.getElementById(`content-library-item-${item.id}`)
  if (targetElement) {
    targetElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }
}

const handleItemsScroll = () => {
  const scrollTop = window.scrollY || document.documentElement.scrollTop || 0
  hasScrolledItems.value = scrollTop > 0
  showTopButton.value = scrollTop > 120
}

const scrollToTop = () => {
  window.scrollTo({ top: 0, behavior: 'smooth' })
}

const getItemTitle = (item) => {
  return item.libraryFileName || item.documentDisplayName || 'Untitled item'
}

const getItemDescription = (item) => {
  return item.description || ''
}

const isFavoriteItem = (itemId) => {
  return favoriteItemIds.value.has(itemId)
}

const isFavoriteActionLoading = (itemId) => {
  return favoriteActionLoadingIds.value.has(itemId)
}

const getFavoriteActionLabel = (itemId) => {
  return isFavoriteItem(itemId) ? 'Remove from Favorites' : 'Add to Favorites'
}

const getItemActionLabel = (item) => {
  return isEmbeddableContentLibraryUrl(item?.embedUrl) ? 'Open Tool' : ''
}

const handleItemAction = (item) => {
  if (!isEmbeddableContentLibraryUrl(item?.embedUrl) || !item?.id) {
    return
  }

  const navigation = customNavigation?.value
  if (!navigation) {
    return
  }

  const query = { itemId: item.id }
  sessionStorageStore.setEvent('contentLibraryEmbeddedToolInternalQuery', query)
  navigation.navigate({
    newPage: navigation.routes.contentLibraryEmbeddedTool.name,
    query
  })
}

const copyZoomPassword = async (password) => {
  if (!password) {
    return
  }

  try {
    await navigator.clipboard.writeText(password)
    copyPasswordSnackbarMessage.value = 'Password copied'

    if (copyPasswordSnackbarTimeoutId) {
      window.clearTimeout(copyPasswordSnackbarTimeoutId)
    }

    copyPasswordSnackbarTimeoutId = window.setTimeout(() => {
      copyPasswordSnackbarMessage.value = ''
      copyPasswordSnackbarTimeoutId = null
    }, 4500)
  } catch (error) {
    handleErrors('BCSContentLibraryTaxonomyItems.copyZoomPassword', error)
  }
}

const hasWhiteLabelingBadge = (category) => {
  return Boolean(logoPath.value && category?.items?.some((item) => isBrandableContentLibraryItem(item)))
}

const getWhiteLabelingTooltipText = (category) => {
  const downloadableItems = (category?.items || []).filter((item) => item.documentFileName || isBrandableContentLibraryItem(item))
  const allDocumentsHaveWhiteLabeling = downloadableItems.length > 0 && downloadableItems.every((item) => isBrandableContentLibraryItem(item))
  return allDocumentsHaveWhiteLabeling
    ? 'All documents in this category have white labeling available.'
    : 'Some documents in this category have white labeling available.'
}

onMounted(async () => {
  if (isContentLibraryLocked.value) {
    redirectToLockedContentLibrary()
    return
  }

  await Promise.all([
    loadGroupedItems(),
    loadFavoriteItemIds(),
    loadDownloadStatsByItemId(),
    loadBranding()
  ])
  scrollToSelectedItem()

  window.addEventListener('scroll', handleItemsScroll, { passive: true })
  handleItemsScroll()
})

onBeforeUnmount(() => {
  window.removeEventListener('scroll', handleItemsScroll)

  if (copyPasswordSnackbarTimeoutId) {
    window.clearTimeout(copyPasswordSnackbarTimeoutId)
    copyPasswordSnackbarTimeoutId = null
  }
})

watch(taxonomyGroupId, async (newTaxonomyGroupId, previousTaxonomyGroupId) => {
  if (isContentLibraryLocked.value) {
    redirectToLockedContentLibrary()
    return
  }

  if (newTaxonomyGroupId && newTaxonomyGroupId !== previousTaxonomyGroupId) {
    await loadGroupedItems()
    scrollToSelectedItem()
  }
})

watch(selectedSortBy, () => {
  syncFilteredCategories()
})

watch(selectedFilters, () => {
  syncFilteredCategories()
}, { deep: true })
</script>

<template>
  <div class="content-library-items-page">
    <div class="content-library-items-list bcs-page-padding">
      <div class="mt-4">
        <div class="content-library-items-breadcrumbs breadcrumbs text-14">
          <p @click="goToHome">Home</p>
          <span>></span>
          <template v-if="hasCoreAccess">
            <p @click="goToHelpAndResources">Help &amp; Resources Center</p>
            <span>></span>
          </template>
          <p @click="customNavigation.navigate({ newPage: customNavigation.routes.contentLibrary.name })">BCS Content
            Library</p>
          <span>></span>
          <p class="disabled">{{ breadcrumbTitle }}</p>
        </div>

        <button type="button"
          class="btn btn-link p-0 mb-3 text-14 text-primary text-decoration-none content-library-back-button"
          @click="goBackToContentLibraryMain">
          <i class="bi bi-chevron-left me-1"></i>
          <strong>Back to BCS Content Library Main</strong>
        </button>

        <AlertNotification v-if="downloadAlert.visible" :showAlert="downloadAlert.visible" :typeAlert="downloadAlert.type"
          class="content-library-download-alert" @update:showAlert="dismissDownloadAlert">
          <span>{{ downloadAlert.message }}</span>
        </AlertNotification>

        <div class="content-library-items-sticky-header" :class="{ 'is-scrolled': hasScrolledItems }">
          <div class="content-library-items-search">
            <BcsSearchDropdown placeholder="Search the BCS Content Library by keyword or sentence"
              :items="searchableItems" :searchFields="['title', 'description']" :maxSuggestions="10"
              :resultsCountOverride="activeSearchItemIds ? filteredResultsCount : null" :arrayTerm="'title'"
              @search="handleSearch" @select="handleSelect" @clear="handleClear" />
          </div>

          <p class="content-library-items-title text-28">{{ taxonomyGroupDetails?.name }}</p>
          <p class="content-library-items-description text-14">{{ taxonomyGroupDetails?.description }}</p>

          <div class="content-library-items-toolbar">
            <div class="content-library-items-toolbar-group">
              <span class="content-library-sort-label text-14">Filter By</span>
              <div v-for="(filterDefinition, index) in countedFilterDefinitions" :key="filterDefinition.key"
                class="content-library-sort-wrap content-library-sort-wrap--filter">
                <FilterDropdown :ref="(element) => setFilterDropdownRef(element, index)" :id="filterDefinition.id"
                  v-model="selectedFilters[filterDefinition.key]" :placeholder="filterDefinition.placeholder"
                  :options="filterDefinition.options" :selectedValuePrefix="filterDefinition.selectedValuePrefix"
                  width="w-100" />
              </div>
              <button v-if="hasSelectedFilters" type="button"
                class="text-primary text-decoration-none ms-2 content-library-reset-filters-link" @click="resetFilters">
                <med>Reset Filters</med>
              </button>
            </div>
            <div class="content-library-items-toolbar-group content-library-items-toolbar-group--sort">
              <span class="content-library-sort-label text-14">Sort By</span>
              <div class="content-library-sort-wrap">
                <FilterDropdown id="contentLibrarySortBy" v-model="selectedSortBy" :options="sortByOptions"
                  :hasDefaultSelection="true" width="w-100" />
              </div>
            </div>
          </div>
        </div>

        <div class="content-library-items-scroll-region">
          <div v-if="loading" class="content-library-items-empty text-14">Loading content...</div>
          <div v-else-if="!filteredCategories.length" class="content-library-items-empty text-14">
            No content items were found for this section.
          </div>

          <div v-else class="content-library-items-groups">
            <section v-for="category in filteredCategories" :key="category.id" class="content-library-category-section">
              <div class="content-library-category-header">
                <div class="content-library-category-title-row">
                  <p class="content-library-category-title text-20">{{ category.name }}</p>
                  <BcsTooltip v-if="hasWhiteLabelingBadge(category)" :simpleText="getWhiteLabelingTooltipText(category)"
                    :showArrow="true" placement="top">
                    <span class="content-library-white-label-badge text-16 text-primary ms-2 d-flex">
                      <img :src="whiteLabelingAvailableIcon" alt="" class="content-library-white-label-icon" />
                      <strong>
                        White Labeling Available
                      </strong>
                    </span>
                  </BcsTooltip>
                </div>
                <p v-if="category.description" class="content-library-category-description text-14">{{
                  category.description }}</p>
              </div>

              <div class="content-library-category-items">
                <div class="content-library-items-grid">
                  <ContentLibraryItemCard v-for="item in category.items" :key="item.id" :item="item"
                    :elementId="`content-library-item-${item.id}`" :title="getItemTitle(item)"
                    :description="getItemDescription(item)" :documentActionLabel="getDocumentActionLabel(item)"
                    :documentActionIcon="getDocumentActionIcon(item)" :documentActions="getDocumentActions(item)"
                    :actionLabel="getItemActionLabel(item)"
                    :favoriteActionLabel="getFavoriteActionLabel(item.id)" :isFavorite="isFavoriteItem(item.id)"
                    :isFavoriteActionLoading="isFavoriteActionLoading(item.id)" @copy-password="copyZoomPassword"
                    @download-document="handleDocumentDownload" @run-action="handleItemAction"
                    @toggle-favorite="toggleFavoriteItem" />
                </div>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>

    <transition name="fade-top-button">
      <button v-if="showTopButton" type="button" class="footer-top-btn" title="Top" @click="scrollToTop">
        <i class="bi bi-chevron-up"></i>
        <span>Top</span>
      </button>
    </transition>

    <transition name="content-library-copy-snackbar">
      <div v-if="copyPasswordSnackbarMessage" class="content-library-copy-snackbar" role="status" aria-live="polite">
        <i class="bi bi-check-circle-fill me-2"></i>
        <span>{{ copyPasswordSnackbarMessage }}</span>
      </div>
    </transition>

    <WhiteLabelModal :handleDownload="downloadPendingBrandingDocument" :onCloseModal="closeWhiteLabelModal"
      :logoPath="logoPath" :logoSize="logoSize" :fileType="pendingBrandingDownloadFileType" ref="whiteLabelModalRef" />

    <Footer :showMarginTop="false"></Footer>
  </div>
</template>

<style scoped lang="scss">
.content-library-items-page {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
}

.content-library-items-list {
  flex: 1 0 auto;
}

.content-library-items-breadcrumbs {
  display: flex;
  gap: 6px;
  margin-left: 6px;
  margin-bottom: 20px;

  p {
    margin: 0;
  }

  p:not(.disabled) {
    cursor: pointer;
    color: var(--bs-primary);
  }

  .disabled {
    color: var(--bs-gray-600);
    cursor: text;
    text-decoration: none;
  }
}

.content-library-items-search {
  margin-top: 14px;
  margin-bottom: 18px;
  margin-left: 8px;
  width: calc(100% - 154px);
  max-width: none;
}

.content-library-reset-filters-link {
  background: none;
  border: 0;
  padding: 0;
  font-size: 0.9rem;
  white-space: nowrap;
  align-self: center;
  border-radius: 4px;

  &:hover {
    color: #9457fe !important;
  }

  &:focus-visible {
    color: #9457fe !important;
    outline: 0;
    box-shadow: 0 0 0 0.25rem #7B61FF66;
  }
}

.content-library-items-sticky-header {
  position: sticky;
  top: 50px;
  z-index: 5;
  background: #fff;
  overflow: visible;
  padding: 16px 0 16px;
  margin-left: calc(50% - 50vw);
  margin-right: calc(50% - 50vw);
  padding-left: calc(50vw - 50%);
  padding-right: calc(50vw - 50%);
  transition: box-shadow 0.2s ease;
}

.content-library-items-sticky-header::after {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 1px;
  background: transparent;
  transition: background-color 0.2s ease;
  pointer-events: none;
}

.content-library-items-sticky-header.is-scrolled {
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.26);
}

.content-library-items-sticky-header.is-scrolled::after {
  background: rgba(15, 23, 42, 0.08);
}

.content-library-items-toolbar {
  display: flex;
  align-items: center;
  gap: 12px;
  justify-content: space-between;
  margin: -14px 147px 20px 8px;
  flex-wrap: wrap;
}

.content-library-items-toolbar-group {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.content-library-items-toolbar-group--sort {
  margin-left: auto;
}

.content-library-sort-label {
  margin: 0;
  color: var(--bs-gray-700);
  font-size: 1rem !important;
  font-weight: 400 !important;
  position: relative;
  left: -2px;
}

.content-library-sort-wrap {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 240px;
  position: relative;
  top: -2px;
  left: 2px;
}

.content-library-sort-wrap--filter {
  min-width: 200px;
}

.content-library-sort-wrap :deep(label) {
  margin-bottom: 4px !important;
  font-size: 1rem !important;
  font-weight: 400 !important;
}

.content-library-sort-wrap :deep(.dropdown) {
  font-family: inherit;
}

.content-library-sort-wrap :deep(.btn-filter-default),
.content-library-sort-wrap :deep(.btn-filter-selected) {
  min-height: 36px;
  padding: 0.25rem 0.625rem !important;
  font-family: inherit !important;
}

.content-library-sort-wrap :deep(.btn.py-2) {
  padding-top: 0.25rem !important;
  padding-bottom: 0.25rem !important;
}

.content-library-sort-wrap--filter :deep(.btn-filter-default.show),
.content-library-sort-wrap--filter :deep(.btn-filter-default:focus),
.content-library-sort-wrap--filter :deep(.btn-filter-default:active) {
  background-color: rgba(111, 66, 193, 0.14) !important;
  border-color: var(--bs-primary) !important;
  color: var(--bs-primary) !important;
}

.content-library-sort-wrap--filter :deep(.btn-filter-default.show .bi-caret-down-fill),
.content-library-sort-wrap--filter :deep(.btn-filter-default:focus .bi-caret-down-fill),
.content-library-sort-wrap--filter :deep(.btn-filter-default:active .bi-caret-down-fill) {
  color: var(--bs-primary) !important;
}

.content-library-sort-wrap :deep(.btn-filter-default .header-20),
.content-library-sort-wrap :deep(.btn-filter-selected .header-20),
.content-library-sort-wrap :deep(.btn-filter-default .header-20 med),
.content-library-sort-wrap :deep(.btn-filter-selected .header-20 med) {
  font-family: inherit !important;
  font-size: 1rem !important;
  font-weight: 400 !important;
}

.content-library-sort-wrap :deep(.header-20),
.content-library-sort-wrap :deep(.option-label.text-16),
.content-library-sort-wrap :deep(.header-20 med),
.content-library-sort-wrap :deep(.dropdown-item .option-label) {
  font-size: 1rem !important;
  line-height: 1.3 !important;
  font-weight: 400 !important;
}

.content-library-sort-wrap :deep(.dropdown-menu) {
  background: #fff;
  border: 1px solid rgba(139, 92, 246, 0.14);
}

.content-library-sort-wrap :deep(.dropdown-item) {
  color: #6f42c1;
}

.content-library-sort-wrap :deep(.dropdown-item:hover) {
  background-color: #6f42c1;
  color: #ffffff;
}

.content-library-sort-wrap :deep(.dropdown-item:hover .option-label) {
  color: #ffffff !important;
}

.content-library-sort-wrap :deep(.dropdown-item.selected:not(:has(.form-check-input))) {
  background-color: #6f42c1;
}

.content-library-sort-wrap :deep(.dropdown-item.selected:not(:has(.form-check-input)) .option-label) {
  color: #ffffff !important;
}

.content-library-sort-wrap :deep(.bi-caret-down-fill) {
  font-size: 0.75rem;
}

.content-library-back-button {
  margin-left: 8px;
  margin-top: 20px;
}

.content-library-items-title {
  margin: 32px 0 0 6px;
}

.content-library-items-description {
  margin: 8px 0 30px 6px;
  width: calc(100% - 154px);
  max-width: none;
  line-height: 1.4;
}

.content-library-items-scroll-region {
  padding-right: 4px;
}

.content-library-items-groups {
  display: flex;
  flex-direction: column;
  gap: 28px;
  padding-bottom: 28px;
}

.content-library-category-section {
  padding-top: 10px;
}

.content-library-category-section + .content-library-category-section {
  position: relative;
  margin-top: 24px;
  padding-top: 32px;
}

.content-library-category-section + .content-library-category-section::before {
  content: '';
  position: absolute;
  top: 0;
  left: 6px;
  right: 143px;
  height: 2px;
  background: var(--bs-gray-300);
}

.content-library-category-header {
  margin-left: 6px;
  margin-bottom: 16px;
  width: calc(100% - 147px);
  max-width: none;
}

.content-library-category-title-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
}

.content-library-category-title {
  margin: 6px 0 6px;
  font-weight: 700;
  font-size: 22px;
}

.content-library-white-label-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.content-library-white-label-icon {
  width: 19px;
  height: 23px;
  flex: 0 0 auto;
}

.content-library-category-description {
  margin: 0 0 18px;
  color: var(--bs-gray-700);
}

.content-library-category-items {
  margin-left: 6px;
  padding-top: 10px;
  width: calc(100% - 147px);
  max-width: none;
}

.content-library-items-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 18px;
}

.content-library-copy-snackbar {
  position: fixed;
  right: 24px;
  bottom: 24px;
  z-index: 40;
  display: inline-flex;
  align-items: center;
  padding: 12px 16px;
  border-radius: 10px;
  background: #198754;
  color: #ffffff;
  box-shadow: 0 10px 24px rgba(25, 135, 84, 0.28);
  font-size: 14px;
  font-weight: 600;
}

.content-library-copy-snackbar i {
  font-size: 16px;
}

.content-library-copy-snackbar-enter-active,
.content-library-copy-snackbar-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.content-library-copy-snackbar-enter-from,
.content-library-copy-snackbar-leave-to {
  opacity: 0;
  transform: translateY(12px);
}


.footer-top-btn {
  position: fixed;
  left: 50%;
  bottom: 72px;
  transform: translateX(-50%);
  z-index: 30;
  width: 58px;
  height: 58px;
  border: none;
  border-radius: 999px;
  background: var(--bs-primary);
  color: #fff;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0;
  box-shadow: 0 10px 24px rgba(111, 66, 193, 0.28);
  transition: transform 0.2s ease, box-shadow 0.2s ease, opacity 0.2s ease;
}

.footer-top-btn:hover,
.footer-top-btn:focus {
  transform: translateX(-50%) translateY(-2px);
  box-shadow: 0 14px 28px rgba(111, 66, 193, 0.35);
}

.footer-top-btn i {
  font-size: 26px;
  line-height: 1;
  transform: translateY(-3px);
  font-weight: 700;
  -webkit-text-stroke: 1.2px currentColor;
  text-shadow:
    0.6px 0 currentColor,
    -0.6px 0 currentColor,
    0 0.6px currentColor,
    0 -0.6px currentColor,
    0.45px 0.45px currentColor,
    -0.45px 0.45px currentColor,
    0.45px -0.45px currentColor,
    -0.45px -0.45px currentColor;
}

.footer-top-btn span {
  font-size: 14px;
  font-weight: 600;
  line-height: 1;
  transform: translateY(-6px);
}

.fade-top-button-enter-active,
.fade-top-button-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.fade-top-button-enter-from,
.fade-top-button-leave-to {
  opacity: 0;
  transform: translateX(-50%) translateY(8px);
}

@media (max-width: 768px) {
  .content-library-category-section + .content-library-category-section::before {
    right: 0;
  }

  .content-library-items-sticky-header {
    padding-left: 0;
    padding-right: 0;
  }

  .content-library-items-toolbar {
    margin-right: 0;
    margin-left: 8px;
    justify-content: flex-start;
  }

  .content-library-items-description {
    width: calc(100% - 8px);
  }

  .content-library-items-toolbar-group,
  .content-library-items-toolbar-group--sort {
    width: 100%;
    margin-left: 0;
  }

  .content-library-sort-wrap {
    width: calc(100% - 8px);
    min-width: 0;
  }

  .content-library-category-items {
    margin-left: 0;
  }

  .content-library-category-header {
    margin-left: 0;
    width: 100%;
  }

  .footer-top-btn {
    bottom: 56px;
  }
}

.content-library-items-empty {
  color: var(--bs-gray-700);
  min-height: 180px;
  padding-bottom: 48px;
  margin-left: 6px;
}
</style>
